import { createTheme } from '@mui/material/styles';
import {
    default as logo,
    default as miniBookingLogo,
} from '../../../assets/img/Belz/belz_logo.png';
import favicon from '../../../assets/img/Belz/browser_tab_logo.jpg';
import loginBackgroundImage from '../../../assets/img/Belz/logon_background.png';
import hexToRgb from '../../utils/hexToRgb';

// BelZ — monochromatic palette (2025 brand guidelines)
// Black           #000000   Logo bg, hero fills, primary CTA text
// Near Black      #0D0D0D   Page background (dark mode)
// Dark Gray       #1A1A1A   Card / panel background, secondary button
// Border          #2A2A2A   Subtle separators
// Mid Gray        #555555   Body copy, secondary text
// Light Gray      #999999   Captions, meta text
// Rule Gray       #CCCCCC   Body text on dark, dividers
// Off White       #F7F7F7   Light page background
// White           #FFFFFF   Primary button bg, headings on dark, accent
// Status (desaturated): Warning #B45309 · Error #991B1B · Info #1E3A5F · Success #14532D

const BODY_FONT_STACK =
  '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif'
const DISPLAY_FONT_STACK =
  '"Montserrat", "Helvetica Neue", Helvetica, Arial, sans-serif'

let theme = createTheme({
  colorBg: '#ffffff',
  typography: {
    fontFamily: BODY_FONT_STACK,
    h1: { fontFamily: DISPLAY_FONT_STACK },
    h2: { fontFamily: DISPLAY_FONT_STACK },
    h3: { fontFamily: DISPLAY_FONT_STACK },
    h4: { fontFamily: DISPLAY_FONT_STACK },
    h5: { fontFamily: DISPLAY_FONT_STACK },
    h6: { fontFamily: DISPLAY_FONT_STACK },
    button: { fontFamily: DISPLAY_FONT_STACK },
  },
  brand: {
    name: 'BelZ',
    logo: logo,
    sidebarImage: '',
    miniBookingLogo: miniBookingLogo,
    favicon: favicon,
    browserTabName: 'BelZ',
  },
  map: {
    routeColor: '#000000',
    routeColorSatellite: '#FFFFFF',
  },
  palette: {
    brand: {
      main: '#000000',
      light: '#1A1A1A',
      dark: '#000000',
    },
    primary: {
      main: '#000000',
      light: '#1A1A1A',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#2A2A2A',
      dark: '#0D0D0D',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#1E3A5F',
      light: '#2c5388',
      dark: '#152843',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#14532D',
      light: '#1e7a43',
      dark: '#0d3a1f',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#B45309',
      light: '#d8690b',
      dark: '#7e3a06',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#991B1B',
      light: '#bf2222',
      dark: '#6b1313',
      contrastText: '#FFFFFF',
    },
    white: {
      main: '#FFFFFF',
      light: '#F7F7F7',
      dark: '#CCCCCC',
      contrastText: '#000000',
    },
    rose: {
      50: '#EC407A',
      100: '#EB3573',
      200: '#E91E63',
      300: '#D81B60',
      400: '#C2185B',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
})

theme = createTheme(theme, {
  pages: {
    loginPage: {
      backgroundImage: 'url(' + loginBackgroundImage + ')',
      titleHeaderInnerColor: theme.palette.primary.main,
      titleHeaderOuterColor: theme.palette.primary.main,
      buttonColor: theme.palette.primary.main,
      loginLogo: miniBookingLogo,
    },
  },
})

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          fontFamily: BODY_FONT_STACK,
        },
        '.react-select__control, .react-select__menu, [class*="-control"], [class*="-menu"], [class*="-singleValue"], [class*="-placeholder"], [class*="-option"], [class*="-input"]':
          {
            fontFamily: `${BODY_FONT_STACK} !important`,
            '& *': {
              fontFamily: `${BODY_FONT_STACK} !important`,
            },
          },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontFamily: DISPLAY_FONT_STACK,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          minHeight: 'auto',
          minWidth: 'auto',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          boxShadow:
            '0 2px 2px 0 rgba(' +
            hexToRgb(theme.palette.common.black) +
            ', 0.14), 0 3px 1px -2px rgba(' +
            hexToRgb(theme.palette.common.black) +
            ', 0.2), 0 1px 5px 0 rgba(' +
            hexToRgb(theme.palette.common.black) +
            ', 0.12)',
          border: 'none',
          borderRadius: '3px',
          position: 'relative',
          padding: '9.6px 24px',
          margin: '.3125rem 1px',
          fontFamily: DISPLAY_FONT_STACK,
          fontSize: '11px',
          fontWeight: '700',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          willChange: 'box-shadow, transform',
          transition:
            'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          lineHeight: '1.42857143',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
          touchAction: 'manipulation',
          cursor: 'pointer',
          '&:hover,&:focus': {
            color: theme.palette.common.white,
            backgroundColor: '#1A1A1A',
            boxShadow:
              '0 14px 26px -12px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.42), 0 4px 23px 0px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.12), 0 8px 10px -5px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.2)',
          },
          '& .fab,& .fas,& .far,& .fal,& .material-icons': {
            position: 'relative',
            display: 'inline-block',
            top: '0',
            marginTop: '-1em',
            marginBottom: '-1em',
            fontSize: '1.1rem',
            marginRight: '4px',
            verticalAlign: 'middle',
          },
          '& svg': {
            position: 'relative',
            display: 'inline-block',
            top: '0',
            width: '18px',
            height: '18px',
            marginRight: '4px',
            verticalAlign: 'middle',
          },
          '&$justicon': {
            '& .fab,& .fas,& .far,& .fal,& .material-icons': {
              marginTop: '0px',
              position: 'absolute',
              width: '100%',
              transform: 'none',
              left: '0px',
              top: '0px',
              height: '100%',
              lineHeight: '41px',
              fontSize: '20px',
            },
          },
          ...(ownerState?.simple && {
            '&,&:focus,&:hover': {
              color: theme.palette.common.white,
              background: 'transparent',
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.simple &&
            ownerState?.color && {
              color:
                theme.palette[ownerState?.color]?.main ||
                theme.palette.common.white,
            }),
          ...(ownerState?.justicon && {
            paddingLeft: '12px',
            paddingRight: '12px',
            fontSize: '20px',
            height: '41px',
            minWidth: '41px',
            width: '41px',
            '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
              marginRight: '0px',
            },
            '&$lg': {
              height: '57px',
              minWidth: '57px',
              width: '57px',
              lineHeight: '56px',
              '& .fab,& .fas,& .far,& .fal,& .material-icons': {
                fontSize: '32px',
                lineHeight: '56px',
              },
              '& svg': {
                width: '32px',
                height: '32px',
              },
            },
            '&$sm': {
              height: '30px',
              minWidth: '30px',
              width: '30px',
              '& .fab,& .fas,& .far,& .fal,& .material-icons': {
                fontSize: '17px',
                lineHeight: '29px',
              },
              '& svg': {
                width: '17px',
                height: '17px',
              },
            },
          }),
          ...(ownerState?.size === 'sm' && {
            '&$justicon': {
              '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
                marginTop: '1px',
              },
            },
            padding: '0.40625rem 1.25rem',
            fontSize: '0.6875rem',
            lineHeight: '1.5',
          }),
          ...(ownerState?.size === 'lg' && {
            '&$justicon': {
              '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
                marginTop: '-4px',
              },
            },
            padding: '1.125rem 2.25rem',
            fontSize: '0.875rem',
            lineHeight: '1.333333',
            borderRadius: '0.2rem',
          }),
          ...(ownerState?.round && {
            borderRadius: '30px',
          }),
          ...(ownerState?.color && {
            boxShadow:
              '0 2px 2px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.14), 0 3px 1px -2px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.2), 0 1px 5px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.12)',
            '&:hover,&:focus': {
              backgroundColor:
                theme.palette[ownerState?.color]?.dark ||
                theme.palette[ownerState?.color]?.main ||
                theme.palette.common.black,
              boxShadow:
                '0 14px 26px -12px rgba(' +
                hexToRgb(
                  theme.palette[ownerState?.color]?.main ||
                    theme.palette.common.black,
                ) +
                ', 0.42), 0 4px 23px 0px rgba(' +
                hexToRgb(theme.palette.common.black) +
                ', 0.12), 0 8px 10px -5px rgba(' +
                hexToRgb(
                  theme.palette[ownerState?.color]?.main ||
                    theme.palette.common.black,
                ) +
                ', 0.2)',
            },
          }),
          ...(ownerState?.color === 'primary' && {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          }),
          ...(ownerState?.color === 'secondary' && {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.common.black,
            border: `1px solid ${theme.palette.common.black}`,
          }),
          ...(ownerState?.color === 'error' && {
            backgroundColor: theme.palette.error.main,
          }),
          ...(ownerState?.color === 'info' && {
            fontFamily: DISPLAY_FONT_STACK,
            color: theme.palette.common.black,
            backgroundColor: theme.palette.common.white,
            border: `2px solid ${theme.palette.common.black}`,
            letterSpacing: '0.08em',
          }),
          ...(ownerState?.color === 'white' && {
            boxShadow:
              '0 2px 2px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.14), 0 3px 1px -2px rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.2), 0 1px 5px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ', 0.12)',
            '&:hover,&:focus': {
              backgroundColor: theme.palette.white.light,
              boxShadow:
                '0 14px 26px -12px rgba(' +
                hexToRgb(theme.palette.common.black) +
                ', 0.42), 0 4px 23px 0px rgba(' +
                hexToRgb(theme.palette.common.black) +
                ', 0.12), 0 8px 10px -5px rgba(' +
                hexToRgb(theme.palette.common.black) +
                ', 0.2)',
            },
            '&,&:focus,&:hover': {
              backgroundColor: theme.palette.white.main,
              color: theme.palette.common.black,
            },
          }),
          ...(ownerState?.transparent && {
            '&,&:focus,&:hover': {
              color: 'inherit',
              background: 'transparent',
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.disabled && {
            opacity: '0.65',
            pointerEvents: 'none',
          }),
          ...(ownerState?.outlined && {
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            background: 'transparent',
            color: theme.palette.primary.main,
            '&:focus,&:hover': {
              color: theme.palette.common.white,
              background: theme.palette.primary.main,
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.text && {
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            padding: '5px',
            margin: 0,
            '&:focus,&:hover': {
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
            },
            opacity: ownerState.disabled ? 0.5 : 1,
          }),
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: 0,
          backgroundColor: theme.palette.common.white,
          borderRadius: 4,
          position: 'relative',
          border: '1px solid #CCCCCC',
          fontSize: 16,
          fontFamily: BODY_FONT_STACK,
          '&:focus': {
            boxShadow: `rgba(${hexToRgb(
              theme.palette.primary.main,
            )}, 0.25) 0 0 0 0.2rem`,
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.common.black,
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: '0',
          padding: '0',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: {
          padding: '0',
          margin: '0',
        },
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          margin: '0',
          padding: '0',
        },
      },
    },
    Sidebar: {
      styleOverrides: {
        overlay: {
          background: theme.palette.brand.main,
        },
      },
    },
    bookingSummary: {
      backgroundImage: theme.palette.white.light,
      backgroundImagePrice: theme.palette.white.light,
      headerColour: theme.palette.brand.main,
      iconLineComplete: theme.palette.white.main,
      iconLinePending: '#999999',
      iconFillComplete: theme.palette.common.black,
      iconFillPending: '#999999',
      sectionTitle: theme.palette.common.black,
      sectionAnswerComplete: '#1A1A1A',
      sectionAnswerPending: '#999999',
      priceValue: '#1A1A1A',
    },
    journeyStatus: {
      headerColour: theme.palette.brand.main,
      headerTextColour: theme.palette.common.white,
    },
    BookingTable: {
      headerColour: theme.palette.brand.main,
    },
    CardHeader: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          const simple = ownerState?.simple

          const style = {
            background: simple
              ? theme.pages.loginPage.titleHeaderOuterColor
              : `linear-gradient(60deg, ${theme.palette[color]?.dark}, ${theme.palette[color]?.light})`,
          }

          if (color !== 'white') {
            style.boxShadow =
              '0 4px 20px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ',.14), 0 7px 10px -5px rgba(' +
              hexToRgb(theme.pages.loginPage.titleHeaderOuterColor) +
              ',.4)'
          }

          return style
        },
      },
    },
    CardIcon: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          const simple = ownerState?.simple

          const style = {
            background: simple
              ? theme.palette[color].main
              : `linear-gradient(60deg, ${theme.palette[color]?.dark}, ${theme.palette[color]?.light})`,
          }

          if (color !== 'white') {
            style.boxShadow =
              '0 4px 20px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ',.14), 0 7px 10px -5px rgba(' +
              hexToRgb(theme.palette[color]?.dark) +
              ',.4)'
          }

          return style
        },
      },
    },
  },
  tableComponents: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          textAlign: 'left',
          textTransform: 'none',
          padding: '0',
          justifyContent: 'flex-start',
          '&:hover': {
            background: 'unset',
          },
        },
      },
    },
  },
})

export default theme
